// Server-owned account identity (APP-ACC-09 / SYS-SW-07). The ONLY source of
// role in the app: fetched from the API (GET /auth/me — the mock mirrors it),
// never chosen client-side. Screens read `useUser()`; Login calls `refresh()`
// after a successful sign-in / sign-up / 2FA verify so the shell picks up the
// new identity, and MenuHub calls it after an installer application.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { api } from '../api'
import type { User } from '../api'

interface UserCtx {
  user: User | null
  /** true until the first fetch resolves — guards must not redirect yet. */
  loading: boolean
  /** Awaitable: callers that NAVIGATE right after an identity change (Login →
   *  sign-in/2FA/sign-up) must await it, so a role-gated route entered next never
   *  sees the stale previous identity and bounces (the RoleGuard reads this
   *  context). Fire-and-forget remains fine where no navigation follows. */
  refresh: () => Promise<User | null>
}

const Ctx = createContext<UserCtx>({ user: null, loading: true, refresh: async () => null })

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const seq = useRef(0) // latest-request-wins (same pattern as the mode toggles)
  const refresh = useCallback(async () => {
    const s = ++seq.current
    try {
      const u = await api.getCurrentUser()
      if (s === seq.current) {
        setUser(u)
        setLoading(false)
      }
      return u
    } catch {
      // A transient fetch failure (getCurrentUser rethrows non-401 errors);
      // keep the last-known identity so a network blip doesn't bounce the user
      // out of a role-gated screen. A genuine sign-out returns null (resolves,
      // not rejects) and clears the user via the assignment above.
      if (s === seq.current) setLoading(false)
      return null
    }
  }, [])
  useEffect(() => {
    void refresh()
  }, [refresh])
  return <Ctx.Provider value={{ user, loading, refresh }}>{children}</Ctx.Provider>
}

export const useUser = () => useContext(Ctx)
